import React, { useState, useEffect } from 'react';
import {
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Typography,
  Box,
  Avatar,
  ListItemAvatar,
  ListItemText,
  Divider,
  List,
  ListItem,
  ListItemButton,
  Button
} from '@mui/material';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import { useNavigate } from 'react-router-dom';
import { getNotifications, markNotificationsAsRead, getUnreadNotificationCount } from '../../services/api';
import { formatRelativeTime } from '../../utils/dateUtils';

interface Notification {
  id: number;
  type: number;
  detail_type: number | null;
  // システム通知（type=5）は送信者がいないため null になる
  from_user: {
    user_id: number | null;
    username: string;
    display_name: string;
    avatar: string | null;
  } | null;
  metadata?: {
    event?: string;
    reason?: string;
    [key: string]: any;
  } | null;
  review?: {
    id: number;
    game_id: number;
    game_name: string;
  };
  memory?: {
    id: number;
    game_id: number;
    game_name: string;
  };
  activity?: {
    id: number;
    user_id: number;
    game_id: number | null;
    game_name: string | null;
  };
  // ギャラリー画像へのリアクション（target_type=5）
  gallery?: {
    id: number;
    thumb_url: string | null;
  };
  is_read: boolean;
  created_at: string;
}

// リアクションの絵文字マッピング
const reactionEmojis: { [key: number]: string } = {
  1: '👍',
  2: '❤️',
  3: '😆',
  4: '😍',
  5: '⚔️',
  6: '😱',
  7: '🚀',
  8: '🤣',
  9: '🎮',
  10: '🔥',
  11: '👏',
  12: '🎉',
  13: '😮',
  14: '😭',
  15: '👀',
  16: '💯',
  17: '🏆'
};

const NotificationsMenu: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [hasMoreUnread, setHasMoreUnread] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // 軽量な未読数チェック（ポーリング用）
  const fetchUnreadCount = async () => {
    try {
      const response = await getUnreadNotificationCount();
      setUnreadCount(response.count);
      setHasMoreUnread(response.has_more);
    } catch (error) {
      console.error('未読通知数の取得に失敗しました:', error);
    }
  };

  // 通知一覧の取得（メニュー開いた時用）
  const fetchNotifications = async (pageNum: number = 1) => {
    try {
      setLoading(true);
      const response = await getNotifications(pageNum);
      if (pageNum === 1) {
        setNotifications(response.results);
      } else {
        setNotifications(prev => [...prev, ...response.results]);
      }
      setHasMore(response.next);
    } catch (error) {
      console.error('通知の取得に失敗しました:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = () => {
    if (!loading && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchNotifications(nextPage);
    }
  };

  useEffect(() => {
    // 初回は未読数のみ取得
    fetchUnreadCount();
    // 1分ごとに未読数を更新（軽量エンドポイント）
    const interval = setInterval(fetchUnreadCount, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    // メニューを開いた時に通知一覧を取得
    fetchNotifications(1);
    setPage(1);
    if (unreadCount > 0 || hasMoreUnread) {
      markNotificationsAsRead().catch(error => {
        console.error('通知の既読処理に失敗しました:', error);
      });
      setUnreadCount(0);
      setHasMoreUnread(false);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = (notification: Notification) => {
    if (notification.type === 5) { // システム通知（送信者なし）
      if (notification.metadata?.event === 'steam_sync_auto_disabled') {
        navigate('/profile/settings?tab=steam');
      }
      handleClose();
      return;
    }
    if (notification.gallery) {
      // 通知の受け手＝画像の所有者なので、自分のプロフィール(/profile)のギャラリータブを開く。
      // /profile/:username は公開ビューになるので使わない。
      navigate(`/profile?tab=gallery&image=${notification.gallery.id}`);
    } else if (notification.review) {
      navigate(`/game/${notification.review.game_id}/reviews/${notification.review.id}`);
    } else if (notification.memory) {
      navigate(`/game/${notification.memory.game_id}/memories/${notification.memory.id}`);
    } else if (notification.activity) {
      navigate(`/activity/${notification.activity.user_id}/${notification.activity.id}`);
    } else if (notification.type === 2 && notification.from_user) { // フォロー通知の場合
      navigate(`/profile/${notification.from_user.username}`);
    }
    handleClose();
  };

  const handleUserClick = (event: React.MouseEvent, username: string) => {
    event.stopPropagation();
    navigate(`/profile/${username}`);
    handleClose();
  };

  // システム通知（from_user なし）の本文
  const getSystemText = (notification: Notification): string => {
    switch (notification.metadata?.event) {
      case 'steam_sync_auto_disabled':
        return 'Steam同期が連続で失敗したため、自動同期をオフにしました。設定を確認してください。';
      default:
        return notification.metadata?.reason || 'システムからのお知らせがあります';
    }
  };

  const getNotificationText = (notification: Notification): string => {
    switch (notification.type) {
      case 1:
        if (notification.memory) {
          return 'さんがメモリーにいいね！しました';
        } else {
          return 'さんがレビューにいいね！しました';
        }
      case 2:
        return 'さんがフォローしました';
      case 3:
        return 'さんがメンションしました';
      case 4: // リアクション
        const emoji = notification.detail_type ? reactionEmojis[notification.detail_type] || '' : '';
        return `さんが${emoji}でリアクションしました`;
      default:
        return '';
    }
  };

  return (
    <>
      <IconButton
        onClick={handleClick}
        size="medium"
        color="inherit"
        sx={{ ml: { xs: 0, md: 1 } }}
      >
        <Badge badgeContent={hasMoreUnread ? '10+' : unreadCount} color="primary">
          <NotificationsNoneIcon />
        </Badge>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: 360,
            maxHeight: 480,
            boxShadow: '0 8px 24px rgba(140,149,159,0.2)',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '6px',
            mt: 0.5
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ p: 1 }}>
          <List sx={{ p: 0 }}>
            {notifications.length > 0 ? (
              notifications.map((notification) => {
                const fromUser = notification.from_user;
                return (
                <ListItem
                  key={notification.id}
                  disablePadding
                >
                  <ListItemButton
                    onClick={() => handleNotificationClick(notification)}
                    sx={{
                      py: 0.5,
                      borderRadius: 1,
                      backgroundColor: notification.is_read ? 'inherit' : 'action.hover',
                      '&:hover': { backgroundColor: 'action.hover' }
                    }}
                  >
                    <ListItemAvatar>
                      {fromUser ? (
                        <Avatar
                          src={fromUser.avatar ? `https://media.plsdb.com/avatars/${fromUser.user_id}/${fromUser.avatar}.jpg` : undefined}
                          alt={fromUser.display_name}
                          sx={{
                            width: 36,
                            height: 36,
                            cursor: 'pointer',
                            '&:hover': {
                              opacity: 0.8,
                            }
                          }}
                          onClick={(e) => handleUserClick(e, fromUser.username)}
                        />
                      ) : (
                        <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main' }}>
                          <NotificationsNoneIcon fontSize="small" />
                        </Avatar>
                      )}
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box component="span" sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
                            {fromUser ? (
                              <>
                                <Box
                                  component="span"
                                  sx={{
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    '&:hover': {
                                      color: 'primary.main',
                                    }
                                  }}
                                  onClick={(e) => handleUserClick(e, fromUser.username)}
                                >
                                  {fromUser.display_name}
                                </Box>
                                {getNotificationText(notification)}
                              </>
                            ) : (
                              getSystemText(notification)
                            )}
                          </Typography>
                          {notification.gallery?.thumb_url && (
                            <Box
                              component="img"
                              src={notification.gallery.thumb_url}
                              alt=""
                              loading="lazy"
                              sx={{
                                mt: 0.5, width: 44, height: 44,
                                objectFit: 'cover', borderRadius: '6px', display: 'block',
                              }}
                            />
                          )}
                          {(notification.review?.game_name || notification.memory?.game_name || notification.activity?.game_name) && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mt: 0.5, fontSize: '0.75rem' }}
                            >
                              {notification.review?.game_name || notification.memory?.game_name || notification.activity?.game_name}
                            </Typography>
                          )}
                        </Box>
                      }
                      secondary={
                        <Typography 
                          variant="caption" 
                          color="text.secondary"
                          sx={{ mt: 0.5, display: 'block', fontSize: '0.7rem' }}
                        >
                          {formatRelativeTime(notification.created_at)}
                        </Typography>
                      }
                    />
                  </ListItemButton>
                </ListItem>
                );
              })
            ) : (
              <ListItem disablePadding>
                <ListItemButton disabled sx={{ py: 0.5, borderRadius: 1 }}>
                  <ListItemText
                    primary={
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        sx={{ textAlign: 'center' }}
                      >
                        通知はありません
                      </Typography>
                    }
                  />
                </ListItemButton>
              </ListItem>
            )}
            {hasMore && (
              <ListItem>
                <Button
                  onClick={loadMore}
                  disabled={loading}
                  fullWidth
                  size="small"
                >
                  {loading ? '読み込み中...' : 'もっと見る'}
                </Button>
              </ListItem>
            )}
          </List>
        </Box>
      </Menu>
    </>
  );
};

export default NotificationsMenu; 